import { toast as reactToastify } from 'react-toastify';

// Track active toast messages to prevent duplicates
const activeToasts = new Map();

/**
 * Normalize a message for comparison
 * Handles strings, React elements, and other types
 */
const normalizeMessage = (message) => {
  if (typeof message === 'string') {
    return message.trim().toLowerCase();
  }
  
  // For React elements, try to extract text content
  if (typeof message === 'object' && message !== null) {
    // If it's a React element with children, try to extract text
    if (message.props) {
      const extractText = (node) => {
        if (typeof node === 'string') return node;
        if (typeof node === 'number') return String(node);
        if (Array.isArray(node)) {
          return node.map(extractText).join(' ').trim();
        }
        if (node && typeof node === 'object' && node.props) {
          if (node.props.children) {
            return extractText(node.props.children);
          }
        }
        return '';
      };
      const text = extractText(message);
      return text.trim().toLowerCase();
    }
    
    // For other objects, stringify and normalize
    return JSON.stringify(message).trim().toLowerCase();
  }
  
  // Fallback: convert to string and normalize
  return String(message).trim().toLowerCase();
};

/**
 * Generate a unique key for a toast message
 */
const getToastKey = (type, message) => {
  const normalized = normalizeMessage(message);
  return `${type}:${normalized}`;
};

/**
 * Wrapper for toast functions that prevents duplicates
 */
const createToastWrapper = (type) => {
  return (message, options = {}) => {
    const toastKey = getToastKey(type, message);
    
    // Check if a similar toast is already active
    if (activeToasts.has(toastKey)) {
      const existingToastId = activeToasts.get(toastKey);
      // Verify the toast is still actually active in react-toastify
      if (reactToastify.isActive(existingToastId)) {
        // Toast with this message is already showing, don't show duplicate
        return existingToastId;
      } else {
        // Toast was dismissed but not cleaned up, remove from tracking
        activeToasts.delete(toastKey);
      }
    }
    
    // Create a unique toast ID
    const toastId = options.toastId || `${type}-${Date.now()}-${Math.random()}`;
    
    // Enhanced options with onClose callback to clean up tracking
    const enhancedOptions = {
      ...options,
      toastId,
      onClose: () => {
        // Remove from active toasts when dismissed
        activeToasts.delete(toastKey);
        // Call original onClose if provided
        if (options.onClose) {
          options.onClose();
        }
      },
    };
    
    // Show the toast using react-toastify
    const toastInstance = reactToastify[type](message, enhancedOptions);
    
    // Track this toast
    activeToasts.set(toastKey, toastInstance);
    
    return toastInstance;
  };
};

// Create wrapped toast functions
const toast = {
  success: createToastWrapper('success'),
  error: createToastWrapper('error'),
  info: createToastWrapper('info'),
  warning: createToastWrapper('warning'),
  warn: createToastWrapper('warning'), // Alias for warning
  // Preserve other react-toastify methods
  dismiss: reactToastify.dismiss.bind(reactToastify),
  isActive: reactToastify.isActive.bind(reactToastify),
  update: reactToastify.update.bind(reactToastify),
  done: reactToastify.done.bind(reactToastify),
  onChange: reactToastify.onChange.bind(reactToastify),
  // Preserve promise methods
  promise: reactToastify.promise.bind(reactToastify),
  loading: reactToastify.loading.bind(reactToastify),
};

export default toast;

